/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import RestoreFromTrashOutlinedIcon from "@mui/icons-material/RestoreFromTrashOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useSnackbar } from "notistack";
import * as orgAdmin from "../../api/orgAdmin";
import { ConfirmDeleteDialog } from "../apps/drive/components/DriveDialogs";
import { formatBytes, formatDate } from "../apps/drive/format";

/**
 * Every deleted file in the organisation, regardless of who deleted it —
 * WorkDrive's "Manage Deleted Items" under Data Administration. Distinct from
 * the per-user Trash page: an admin can restore or purge on someone else's
 * behalf, which the personal trash deliberately cannot do.
 */
const AdminTrash = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState({ loading: true, files: [], error: null });
  const [confirming, setConfirming] = useState(null); // the file pending permanent delete

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      setState({ loading: false, files: await orgAdmin.listOrgTrash(), error: null });
    } catch (err) {
      setState({ loading: false, files: [], error: err.message || "Could not load the trash." });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const ownerLabel = (file) => file.user_full_name || file.user_email || `user ${file.user_id.slice(0, 8)}…`;

  const restore = async (file) => {
    try {
      await orgAdmin.restoreOrgFile(file.id);
      enqueueSnackbar(`“${file.file_name}” restored for ${ownerLabel(file)}.`, { variant: "success" });
      load();
    } catch (err) {
      enqueueSnackbar(err.message || "Restore failed.", { variant: "error" });
    }
  };

  if (state.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "accent.main" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 600 }}>Deleted items</Typography>
        <Typography sx={{ fontSize: 12.5, color: theme.palette.secondaryText.color }}>
          Files any member of this organisation has deleted. Restoring or purging here
          acts on their behalf.
        </Typography>
      </Box>

      {state.error && <Alert severity="error" sx={{ mb: 2 }}>{state.error}</Alert>}

      <Paper variant="outlined">
        {state.files.length === 0 ? (
          <Typography sx={{ p: 4, textAlign: "center", fontSize: 13.5, color: theme.palette.secondaryText.color }}>
            Nothing in the organisation's trash.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell width={160}>Owner</TableCell>
                <TableCell width={110}>Size</TableCell>
                <TableCell width={140}>Deleted</TableCell>
                <TableCell width={110} />
              </TableRow>
            </TableHead>
            <TableBody>
              {state.files.map((file) => (
                <TableRow key={file.id} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <InsertDriveFileIcon fontSize="small" sx={{ color: "text.color", opacity: 0.5 }} />
                      <Typography variant="body2" noWrap>{file.file_name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar sx={{ width: 20, height: 20, fontSize: 10 }}>
                        {ownerLabel(file).charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography sx={{ fontSize: 12 }}>{ownerLabel(file)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{formatBytes(file.size_bytes)}</TableCell>
                  <TableCell>{formatDate(file.updated_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Restore">
                      <IconButton size="small" onClick={() => restore(file)}>
                        <RestoreFromTrashOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete permanently">
                      <IconButton
                        size="small"
                        sx={{ color: "error.main" }}
                        onClick={() => setConfirming(file)}
                      >
                        <DeleteForeverOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <ConfirmDeleteDialog
        open={Boolean(confirming)}
        items={confirming ? [{ id: confirming.id, name: confirming.file_name, type: "file" }] : []}
        onConfirm={async () => {
          await orgAdmin.deleteOrgFileForever(confirming.id);
          enqueueSnackbar("Permanently deleted.", { variant: "success" });
          load();
        }}
        onClose={() => setConfirming(null)}
      />
    </Box>
  );
};

export default AdminTrash;
